import Model from 'src/app/common/model';
import FeedMealModel from './feedMeal.model';

/**
 * @model CheckTrayReadingModel
 * @description
 * Represents the data structure for recording observations from "Check Trays" (or feed trays) in aquaculture ponds.
 *
 * **Domain Context:**
 * Check trays are a primary method for monitoring shrimp/fish appetite. A specific amount of feed is placed
 * in these trays and submerged. After a set duration, technicians lift the trays to observe how much feed remains.
 * This data is crucial for adjusting the next meal's ration to prevent overfeeding (which spoils water quality)
 * or underfeeding (which stunts growth).
 *
 * **Purpose:**
 * This model captures the specific metrics for a single check tray event, including how much feed was put in,
 * how much was left, and the calculated consumption.
 *
 * **Key Responsibilities:**
 * 1. **Quantification**: Tracks `feed_given` vs `feed_remaining` to calculate consumption.
 * 2. **Identification**: Links the reading to a specific `check_tray_number` and `feed_meal_id`.
 * 3. **Verification**: Supports evidence collection via `imagecount` and `verify_time`.
 *
 * **Data Flow:**
 * - **Input**: Technicians enter this data via mobile app while pond-side.
 * - **Processing**: Used by algorithms to determine the "Appetite Index" or percentage adjustment for the next feed.
 * - **Storage**: Persisted as part of a `FeedLog` or `FeedMeal` record.
 *
 * **User Roles:**
 * - **Technician**: Records the physical observation.
 * - **Supervisor**: Reviews images and data to validate feeding decisions.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class CheckTrayReadingModel extends Model {
  /**
   * @type {FeedMealModel}
   * @description The parent feed meal associated with this check tray reading.
   * @optional
   */
  feedMeal?: FeedMealModel;

  /**
   * @type {Array<IFeedTypes>}
   * @description Detailed breakdown of specific feed items or mixes placed in the tray.
   * @optional
   */
  feedTypes?: Array<IFeedTypes>;

  /**
   * @type {number}
   * @description The sequential number or identifier of the check tray (e.g., 1, 2, 3, 4).
   * Ponds typically have multiple trays to get an average reading.
   * @optional
   */
  check_tray_number?: number;

  /**
   * @type {number}
   * @description Foreign key linking this reading to a specific feed meal event.
   * @optional
   */
  feed_meal_id?: number;

  /**
   * @type {number}
   * @description The theoretical amount of feed required for this tray based on standard protocols.
   * @optional
   */
  check_tray_feed_required?: number;

  /**
   * @type {number}
   * @description The actual amount of feed placed in the tray.
   * @optional
   */
  check_tray_feed_given?: number;

  /**
   * @type {number}
   * @description The amount of feed found remaining in the tray after the check duration.
   * This is the critical value for determining appetite.
   * @optional
   */
  check_tray_feed_remaining?: number;

  /**
   * @type {Date}
   * @description The timestamp when the tray was lifted and verified.
   * @optional
   */
  verify_time?: Date;

  /**
   * @type {number}
   * @description The calculated amount of feed consumed (Given - Remaining).
   * @optional
   */
  check_tray_feed_consumed?: number;

  /**
   * @type {boolean}
   * @description Check Tray Inspection Result (CTIR).
   * Flag indicating if the inspection was successfully completed or valid.
   */
  CTIR: boolean;

  /**
   * @type {number}
   * @description The number of images attached to this reading for verification purposes.
   */
  imagecount: number;

 /**
  * @constructor
  * @description
  * Initializes a new instance of the CheckTrayReadingModel.
  * Sets up the `feedTypes` array to ensure it is ready for data population.
  */
 constructor() {
    super();
    this.feedTypes = new Array();
  }
}

//////////////////////////////
//IFeedTypes
//////////////////////////////
/**
 * @model IFeedTypes
 * @description
 * Helper class defining the specific composition of feed in the tray.
 * Handles cases where multiple feed types or mixes are used.
 */
export class IFeedTypes {
  /**
   * @type {number}
   * @description ID of the specific feed item (pellet size/brand).
   * @optional
   */
  feed_item_id?: number;

  /**
   * @type {number}
   * @description Quantity of this specific feed item given.
   * @optional
   */
  feed_qty_given?: number;

  /**
   * @type {number}
   * @description ID of any mix/additive item included.
   * @optional
   */
  mix_item_id?: number;

  /**
   * @type {number}
   * @description Quantity of the mix item given.
   * @optional
   */
  mix_qty_given?: number;

  /**
   * @constructor
   * @description Initializes a new instance of IFeedTypes.
   */
  constructor() {
  }
}
